import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpJobChangeService from "@plannel/services/fp/fp-jobchange-service";
import ResourcePopup from "@plannel/pages/factory-plan/common/popup/ResourcePopup";
import ItemPopup from "@plannel/pages/factory-plan/common/popup/ItemPopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 3개(resourceCd/prevItemCd/nextItemCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "resourceCd", label: "FP_RESOURCE_CD" },
  { field: "prevItemCd", label: "FP_PREV_ITEM_CD" },
  { field: "nextItemCd", label: "FP_NEXT_ITEM_CD" }
];

/**
 * 품목간 작업 교체(Item Job Change) 탭 — old ItemJobChangeTab.jsx 대응. §11-1(항상 마운트 +
 * display 토글)로 JobChangeTime.js에서 렌더링되므로, planScope가 바뀔 때마다 자체적으로
 * 재조회한다. prevItemCd/nextItemCd 선택 팝업은 resourceCd가 선택된 행에서만 연다(old
 * onCellButtonClicked의 resourceCd 값 가드와 동일).
 *
 * props: t, viewName, planScope, searchFields
 */
const ItemJobChangeTab = ({ t, viewName, planScope, searchFields }) => {
  const gridRef = useRef();
  const [itemJobChange, setItemJobChange] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [resourcePopup, setResourcePopup] = useState({ open: false, node: null });
  const [itemPopup, setItemPopup] = useState({ open: false, node: null, target: "" });

  const divideTpValuesRef = useCommonCodeValues("FP_DIVIDE_TP_CD");
  const timeUomValuesRef = useCommonCodeValues("FP_TIME_UOM");
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_ITEM_JOB_CHANGE"), viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // planScope는 서버 조회, 나머지는 AG Grid 외부 필터로 즉시 필터(§6-1 케이스3).
  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { resourceCdNm, prevItemCd, nextItemCd } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (resourceCdNm && !(hit(r.resourceCd, resourceCdNm) || hit(r.resourceNm, resourceCdNm))) return false;
    if (prevItemCd && !hit(r.prevItemCd, prevItemCd)) return false;
    if (nextItemCd && !hit(r.nextItemCd, nextItemCd)) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const openItemPopup = (node, target) => {
    // old: resourceCd가 없으면 전/후 품목 선택 버튼이 동작하지 않음.
    if (!node.data?.resourceCd) return;
    setItemPopup({ open: true, node, target });
  };

  const columnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old buttonVisibleCallback: isNewGridButtonVisible 대응 — 신규 행에서만 재선택 가능(§3-3).
    { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setResourcePopup({ open: true, node }), hideButtonWhenSaved: true } },
    { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 240 },
    // old buttonVisibleCallback: isGridResourceButtonVisible 대응 — resourceCd 선택 후 + 신규 행에서만(§3-4).
    { headerName: "FP_PREV_ITEM_CD", field: "prevItemCd", colId: "prevItemCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => openItemPopup(node, "prevItemCd"), hideButtonWhenSaved: true, requiredField: "resourceCd" } },
    { headerName: "FP_PREV_ITEM_NM", field: "prevItemNm", colId: "prevItemNm", type: ["nonEditableColumn"], width: 240 },
    { headerName: "FP_NEXT_ITEM_JC_TM", field: "nextJcTime", colId: "nextJcTime", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_PREV_ITEM_JC_TM", field: "prevJcTime", colId: "prevJcTime", width: 110, type: ["numberColumn", "rightAligned"] },
    // old buttonVisibleCallback: isGridResourceButtonVisible 대응 — resourceCd 선택 후 + 신규 행에서만(§3-4).
    { headerName: "FP_NEXT_ITEM_CD", field: "nextItemCd", colId: "nextItemCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => openItemPopup(node, "nextItemCd"), hideButtonWhenSaved: true, requiredField: "resourceCd" } },
    { headerName: "FP_NEXT_ITEM_NM", field: "nextItemNm", colId: "nextItemNm", type: ["nonEditableColumn"], width: 240 },
    { headerName: "FP_TIME_UOM", field: "timeUom", colId: "timeUom", width: 120, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_TIME_UOM", timeUomValuesRef, t) },
    { headerName: "FP_JC_DIVIDE_TP_CD", field: "jcDivideTpCd", colId: "jcDivideTpCd", width: 110, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_DIVIDE_TP_CD", divideTpValuesRef, t) },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveItemJobChange = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpJobChangeService.getItemJobChanges({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setItemJobChange([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setItemJobChange(results);
        }
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    retrieveItemJobChange();
  };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, resourceCd: "", resourceNm: "",
      prevItemCd: "", prevItemNm: "", nextItemCd: "", nextItemNm: "",
      nextJcTime: 0, prevJcTime: 0, timeUom: "MINUTE", jcDivideTpCd: "Y"
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeItemJobChange = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpJobChangeService.removeItemJobChanges(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveItemJobChange());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveItemJobChange = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpJobChangeService.upsertItemJobChanges(allStateData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveItemJobChange());
  };

  const onSelectResource = (row) => {
    const node = resourcePopup.node;
    if (node) {
      node.setDataValue("resourceCd", row.resourceCd);
      node.setDataValue("resourceNm", row.resourceNm);
      // old: 자원이 바뀌면 이전에 선택된 전/후 품목은 더 이상 유효하지 않으므로 초기화.
      node.setDataValue("prevItemCd", "");
      node.setDataValue("prevItemNm", "");
      node.setDataValue("nextItemCd", "");
      node.setDataValue("nextItemNm", "");
    }
  };

  const onSelectItem = (row) => {
    const { node, target } = itemPopup;
    if (node && target === "prevItemCd") {
      node.setDataValue("prevItemCd", row.itemCd);
      node.setDataValue("prevItemNm", row.itemNm);
    } else if (node && target === "nextItemCd") {
      node.setDataValue("nextItemCd", row.itemCd);
      node.setDataValue("nextItemNm", row.itemNm);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveItemJobChange();
    else if (action === "DELETE") removeItemJobChange();
  };

  // 화면 내부 탭(§11-3): planScope 변경 시에만 재조회 — 탭 전환 자체는 재조회를 트리거하지 않음.
  useEffect(() => {
    if (!gridRef.current?.api) return;
    retrieveItemJobChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t("FP_ITEM_JOB_CHANGE")}.xlsx`, sheetName: t("FP_ITEM_JOB_CHANGE") })} />
        <AddButton onClick={insertRow} />
        <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
        <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
      </Box>
      <Box id="fp-item-jc-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={itemJobChange}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <ResourcePopup
        open={resourcePopup.open}
        onClose={() => setResourcePopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectResource}
      />
      <ItemPopup
        open={itemPopup.open}
        onClose={() => setItemPopup({ open: false, node: null, target: "" })}
        planScope={planScope}
        onSelect={onSelectItem}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ItemJobChangeTab);
